<template>
  <div ref="rootRef" class="relative w-fit" data-test-id="cms-base-expandable-cta" @keydown.esc="collapse">
    <vf-button
      ref="triggerRef"
      :id
      :aria-controls="rendered ? contentId : undefined"
      :aria-expanded="hasTargets ? expanded : undefined"
      :aria-label="expanded ? $t.close : undefined"
      :class="{ 'px-0': expanded }"
      :size
      :variant="resolvedVariant"
      @click="toggle"
    >
      <span
        class="grid duration motion-safe:transition-grid-template-columns"
        :class="expanded ? 'cols-[0fr]' : 'cols-[1fr]'"
      >
        <span class="min-w-0 flex overflow-hidden">
          <span class="ws-nowrap">{{ text }}</span>
        </span>
      </span>
      <span
        class="grid duration motion-safe:transition-grid-template-columns"
        :class="expanded ? 'cols-[1fr]' : 'cols-[0fr]'"
      >
        <span class="min-w-0 flex overflow-hidden">
          <vf-icon v-style:mx="iconMargin" name="close" :size="iconSize" />
        </span>
      </span>
    </vf-button>
    <Teleport v-if="rendered" to="body">
      <ul
        ref="listRef"
        :id="contentId"
        :aria-labelledby="id"
        class="pointer-within z-below-header w-max f-col gap-2"
        data-test-id="cms-base-expandable-cta-targets"
        :inert="!expanded || undefined"
        :style="[floatingStyles, { minWidth }]"
        @keydown.esc="collapse"
      >
        <transition-group
          appear
          enter-active-class="motion-safe:delay-$enter-delay"
          enter-from-class="op-0 translate-y-full"
          leave-active-class="motion-safe:delay-$leave-delay"
          leave-to-class="op-0 translate-y-full"
          @after-leave="unmountList"
        >
          <li
            v-for="(target, i) in shownTargets"
            :key="target.id || i"
            class="grid duration-300 motion-safe:transition-[opacity,transform]"
            :style="getDelays(i)"
          >
            <cms-shared-button v-bind="{ ...target, style: resolvedVariant }" @click="$emit('click', i)" />
          </li>
        </transition-group>
      </ul>
    </Teleport>
  </div>
</template>

<script lang="ts" setup>
import type { VfButton } from '#components'
import type { Responsive } from '#types/common'
import type { ButtonSize, ButtonVariant } from '#types/components/vf/button'
import type { IconSizes } from '#types/sizes'
import type { Target } from '#types/target'
import { autoUpdate, offset, shift, useFloating } from '@floating-ui/vue'
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap'

export type Props = {
  size?: ButtonSize | Partial<Responsive<ButtonSize>>
  targets?: Target[]
  text?: string
  variant?: ButtonVariant | Partial<Responsive<ButtonVariant>>
}

const {
  size,
  targets = [],
  variant,
} = defineProps<Props>()

const emit = defineEmits<{
  click: [index: number]
  close: []
  expand: []
}>()

// Temporary fix as per https://github.com/nuxt/nuxt/issues/25755
const id = `cms-base-expandable-cta-${useId().replace('_', '-')}`
const contentId = `${id}-content`

const rootRef = ref<HTMLElement>()
const listRef = ref<HTMLElement>()
const triggerRef = ref<typeof VfButton>()
const expanded = ref(false)
/** Keeps the list in the DOM from expand until its targets finished leaving, so collapsed CTAs render no list. */
const rendered = ref(false)
const minWidth = ref<string>()

const hasTargets = computed(() => targets.length > 0)
const shownTargets = computed(() => expanded.value ? targets : [])
const triggerEl = computed<HTMLElement | undefined>(() => triggerRef.value?.$el)

const { fallbackSizes = {} } = useAppConfig().components.vf.button

/** The sizes whose `vf-button-*` line box is smaller than the default icon, so it never makes the trigger taller. */
const iconSizes: Partial<Record<ButtonSize, IconSizes>> = {
  'extra-tiny': 'sm',
  'tiny': 'sm',
  'xs': 'sm',
}

/**
 * Mirrors each `vf-button-*` vertical padding, so the expanded, icon-only trigger reads as a square.
 * Brands overriding those paddings get a near-square instead - only the width is affected, never the height.
 */
const iconMargins: Record<ButtonSize, string> = {
  'extra-tiny': '0.4375rem',
  'tiny': '0.4375rem',
  'xs': '0.6875rem',
  'sm': '0.6875rem',
  'md': '0.9375rem',
}

/** Resolved the way `vf-button` resolves it, so brand `fallbackSizes` are reflected in the icon too. */
const resolvedSize = computed(() => replaceValues(
  isString(size) ? { sm: size } : { sm: 'sm', ...filterObject(size || {}) },
  fallbackSizes
) as Partial<Responsive<ButtonSize>>)

const iconSize = computed(() => mapObject(resolvedSize.value, (value) => iconSizes[value!] || 'md'))
const iconMargin = computed(() => mapObject(resolvedSize.value, (value) => iconMargins[value!]))

/**
 * Drives the trigger and every target in the list, so the whole control reads as one unit.
 * Takes precedence over each target's own `style`.
 */
const resolvedVariant = computed<Responsive<ButtonVariant>>(() =>
  isString(variant) ? { sm: variant } : { sm: 'primary', ...variant })

const { floatingStyles } = useFloating(triggerEl, listRef, {
  open: expanded,
  placement: 'top-start',
  middleware: [offset(8), shift({ padding: 8 })],
  whileElementsMounted: (reference, floating, update) =>
    autoUpdate(reference, floating, () => expanded.value && update()),
})

const { activate, deactivate } = useFocusTrap([triggerEl, listRef], {
  immediate: false,
  initialFocus: false,
  allowOutsideClick: true,
  escapeDeactivates: false,
  returnFocusOnDeactivate: false,
})

/**
 * Targets roll in bottom to top on expand and leave top to bottom on collapse.
 * Both are set up front, as a leaving target is no longer re-rendered.
 * Only consumed by the `motion-safe:delay-*` transition classes, so reduced motion skips the stagger.
 */
function getDelays(index: number) {
  return {
    '--enter-delay': `${(targets.length - 1 - index) * 60}ms`,
    '--leave-delay': `${index * 40}ms`,
  }
}

/** Removes the list once its last target has left, unless the CTA was expanded again meanwhile. */
function unmountList() {
  if (!expanded.value && !listRef.value?.childElementCount) rendered.value = false
}

function close() {
  if (!expanded.value) return
  expanded.value = false
  deactivate()
  emit('close')
}

function toggle() {
  if (!hasTargets.value) return
  if (expanded.value) return close()
  // measured while the main button still shows its label, so it is the collapsed width
  minWidth.value = `${triggerEl.value?.offsetWidth || 0}px`
  rendered.value = true
  expanded.value = true
  // the list only renders on expand, so the trap needs the updated DOM
  nextTick(activate)
  emit('expand')
}

function collapse() {
  if (!expanded.value) return
  close()
  triggerEl.value?.focus()
}

onClickOutside(rootRef, () => close(), { ignore: [listRef] })
</script>
